import type {
  StoreAddCartLineItem,
  StoreCart,
  StoreCartLineItem,
  StoreUpdateCart,
} from "@medusajs/types";
import {
  queryOptions,
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { z } from "zod";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { medusa } from "#/lib/medusa";
import {
  type AttachCartToCustomer,
  formatMoney,
  getCartItemCount,
  getCartTotals,
  setAttachCartToCustomerImpl,
} from "#/lib/medusa-types/commerce";
import { useSession } from "#/lib/useSession";
import { parseTimestamp } from "#/lib/utils";
import { fetchDefaultRegion } from "#/queries/regions";
import { computeConvenienceFee } from "#/lib/payment-types";
import { readAttribution } from "#/lib/attribution.js";

type CartState = {
  cartId: string | null;
  hasHydrated: boolean;
  lastOrderId: string | null;
  setCartId: (id: string | null) => void;
  setLastOrderId: (id: string | null) => void;
  clearCart: () => void;
  setHasHydrated: (v: boolean) => void;
};

const persistedSchema = z.object({
  cartId: z.string().min(1).nullable(),
});

export const useCartStore = create<CartState>()(
  persist(
    (set) => ({
      cartId: null,
      hasHydrated: false,
      lastOrderId: null,
      setCartId: (id) => set({ cartId: id }),
      setLastOrderId: (id) => set({ lastOrderId: id }),
      clearCart: () => set({ cartId: null }),
      setHasHydrated: (v) => set({ hasHydrated: v }),
    }),
    {
      name: "lp:cart:v2",
      partialize: (state) => ({ cartId: state.cartId }),
      merge: (persistedValue, current) => {
        const parsed = persistedSchema.safeParse(persistedValue);
        if (!parsed.success) return current;
        return { ...current, ...parsed.data };
      },
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    },
  ),
);

export function selectCartId(state: CartState): string | null {
  return state.cartId;
}

export function selectHasHydrated(state: CartState): boolean {
  return state.hasHydrated;
}

export function selectLastOrderId(state: CartState): string | null {
  return state.lastOrderId;
}

export const cartKeys = {
  all: ["storefront", "cart"] as const,
  detail: (cartId: string | null) =>
    ["storefront", "cart", cartId ?? "none"] as const,
};

function sortCartItems(items: StoreCartLineItem[]): StoreCartLineItem[] {
  return [...items].sort((a, b) => {
    const aTime = parseTimestamp(a.created_at);
    const bTime = parseTimestamp(b.created_at);
    if (aTime !== bTime) return aTime - bTime;
    return a.id.localeCompare(b.id);
  });
}

function normalizeCart(cart: StoreCart): StoreCart {
  if (!cart.items || cart.items.length < 2) return cart;
  return { ...cart, items: sortCartItems(cart.items) };
}

async function fetchCart(cartId: string): Promise<StoreCart | null> {
  try {
    const { cart } = await medusa.store.cart.retrieve(cartId);
    // Self-heal: if the persisted cart has been completed (turned into an
    // order) clear it so the next add-to-cart starts fresh.
    if (cart.completed_at) {
      useCartStore.getState().clearCart();
      return null;
    }
    // The gateway-recovery loop stamps this when the gateway confirms money
    // against a cart it can't yet promote (stock block, amount mismatch).
    // Reusing such a cart would overwrite the paid basket and make the
    // payment unrecoverable, so treat it exactly like a completed cart.
    if (cart.metadata?.gateway_paid_hold) {
      useCartStore.getState().clearCart();
      return null;
    }
    return normalizeCart(cart);
  } catch (err) {
    if (
      err != null &&
      typeof err === "object" &&
      "status" in err &&
      (err.status === 404 || err.status === 410)
    ) {
      useCartStore.getState().clearCart();
      return null;
    }
    throw err;
  }
}

let inflightCreate: Promise<StoreCart> | null = null;

async function createCart(): Promise<StoreCart> {
  if (inflightCreate) return inflightCreate;
  inflightCreate = (async () => {
    try {
      const region = await fetchDefaultRegion();
      const { cart } = await medusa.store.cart.create(
        region ? { region_id: region.id } : {},
      );
      useCartStore.getState().setCartId(cart.id);
      return cart;
    } finally {
      inflightCreate = null;
    }
  })();
  return inflightCreate;
}

async function ensureCart(cartId: string | null): Promise<StoreCart> {
  if (cartId) {
    const existing = await fetchCart(cartId);
    if (existing) return existing;
  }
  return createCart();
}

export const cartQueries = {
  detail: (cartId: string | null) =>
    queryOptions({
      queryKey: cartKeys.detail(cartId),
      queryFn: () => (cartId ? fetchCart(cartId) : null),
      enabled: cartId !== null,
      staleTime: 30_000,
    }),
};

export function useFetchCartItemWeight(cartId: string | null) {
  return useQuery({
    queryKey: ["cartItemWeight", cartId],
    queryFn: async () => {
      if (!cartId) throw new Error("No cart ID provided");

      const { cart } = await medusa.store.cart.retrieve(cartId);
      const cartItems = cart.items || [];

      const enrichedItems = await Promise.all(
        cartItems?.map(async (item) => {
          if (!item.product_id) return 0;
          const { product } = await medusa.store.product.retrieve(item.product_id);

          if (product?.weight) return product?.weight * item.quantity
          return 0;
        })
      );

      const totalWeight = enrichedItems.reduce((prev, curr) => prev + curr, 0)
      return totalWeight ?? 0;
    },
    enabled: !!cartId, // Ensures the query only runs if cartId exists
    staleTime: 30_000,
  });
}

export function checkUtmParams(): boolean {
  return Object.keys(readAttribution()).length > 0;
}

export function useCart(): UseQueryResult<StoreCart | null> {
  const cartId = useCartStore(selectCartId);
  return useQuery(cartQueries.detail(cartId));
}

function isMyCartResponse(v: unknown): v is { cart_id: string | null } {
  if (typeof v !== "object" || v === null || !("cart_id" in v)) return false;
  const id = (v as { cart_id: unknown }).cart_id;
  return typeof id === "string" || id === null;
}

export function useRecoverCampaignCart(): UseQueryResult<string | null> {
  const cartId = useCartStore(selectCartId);
  const hasHydrated = useCartStore(selectHasHydrated);
  const { customer } = useSession();
  const { data: cart } = useCart();

  const localIsEmpty =
    cartId === null || (cart !== undefined && (cart?.items?.length ?? 0) === 0);

  return useQuery({
    queryKey: ["storefront", "cart", "recover", customer?.id ?? "guest"],
    queryFn: async () => {
      const res = await medusa.client.fetch<unknown>(
        "/store/customers/me/cart",
      );
      if (!isMyCartResponse(res)) {
        throw new Error("Unexpected my-cart response shape");
      }
      
      if (res.cart_id) useCartStore.getState().setCartId(res.cart_id);
      return res.cart_id;
    },
    enabled:
      hasHydrated && localIsEmpty && customer !== null && checkUtmParams(),
    staleTime: Number.POSITIVE_INFINITY, // one recovery per page load is enough
  });
}

export function useCartCount(): number {
  const { data: cart } = useCart();
  return getCartItemCount(cart);
}

export function useLineForVariant(
  variantId: string | undefined,
): { id: string; quantity: number } | null {
  const { data: cart } = useCart();
  if (!variantId || !cart?.items) return null;
  const item = cart.items.find((l) => l.variant_id === variantId);
  if (!item) return null;
  return { id: item.id, quantity: Number(item.quantity) };
}

export function useCartTotals() {
  const { data: cart } = useCart();
  return getCartTotals(cart);
}

export function useFormatCartMoney(): (amount: number) => string {
  const { data: cart } = useCart();
  const code = cart?.currency_code ?? "php";
  return (amount: number) => formatMoney(amount, code);
}

export type AddToCartInput = StoreAddCartLineItem;

type CartMutationContext = {
  previousCart: StoreCart | null;
  queryKey: ReturnType<typeof cartKeys.detail>;
};

const CART_MUTATION_KEY = ["cart-mutation"] as const;

function buildOptimisticLine(
  parentCart: StoreCart,
  input: AddToCartInput,
): StoreCartLineItem {
  return {
    id: `optimistic-${input.variant_id}-${Date.now()}`,
    title: "",
    quantity: input.quantity,
    requires_shipping: true,
    is_discountable: true,
    is_tax_inclusive: false,
    unit_price: 0,
    cart_id: parentCart.id,
    cart: parentCart,
    variant_id: input.variant_id,
    created_at: new Date(),
  };
}

export function useAddToCart(): UseMutationResult<
  StoreCart,
  Error,
  AddToCartInput,
  CartMutationContext | undefined
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: CART_MUTATION_KEY,
    mutationFn: async (input) => {
      const cartId = useCartStore.getState().cartId;
      const cart = await ensureCart(cartId);
      const { cart: updated } = await medusa.store.cart.createLineItem(
        cart.id,
        input,
      );
      return updated;
    },
    onMutate: async (input) => {
      const cartId = useCartStore.getState().cartId;
      if (!cartId) return undefined;
      const queryKey = cartKeys.detail(cartId);
      await queryClient.cancelQueries({ queryKey });
      const previousCart =
        queryClient.getQueryData<StoreCart | null>(queryKey) ?? null;
      if (!previousCart) return { previousCart: null, queryKey };

      const items = previousCart.items ?? [];
      const existingIdx = items.findIndex(
        (l) => l.variant_id === input.variant_id,
      );
      const nextItems =
        existingIdx >= 0
          ? items.map((l, i) =>
              i === existingIdx
                ? { ...l, quantity: Number(l.quantity) + input.quantity }
                : l,
            )
          : [...items, buildOptimisticLine(previousCart, input)];

      queryClient.setQueryData<StoreCart>(queryKey, {
        ...previousCart,
        items: nextItems,
      });
      return { previousCart, queryKey };
    },
    onError: (_err, _input, context) => {
      if (context) {
        queryClient.setQueryData(context.queryKey, context.previousCart);
      }
    },
    onSettled: () => {
      if (queryClient.isMutating({ mutationKey: CART_MUTATION_KEY }) === 1) {
        return queryClient.invalidateQueries({ queryKey: cartKeys.all });
      }
    },
  });
}

async function deleteLineAndGetCart(
  cartId: string,
  lineItemId: string,
): Promise<StoreCart> {
  const { parent } = await medusa.store.cart.deleteLineItem(cartId, lineItemId);
  if (parent) return normalizeCart(parent);
  const refreshed = await fetchCart(cartId);
  if (!refreshed) throw new Error("Cart not found after line deletion");
  return refreshed;
}

export function useUpdateCartLine(): UseMutationResult<
  StoreCart,
  Error,
  { lineItemId: string; quantity: number },
  CartMutationContext | undefined
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: CART_MUTATION_KEY,
    mutationFn: async ({ lineItemId, quantity }) => {
      const cartId = useCartStore.getState().cartId;
      if (!cartId) throw new Error("No cart");
      if (quantity <= 0) {
        return deleteLineAndGetCart(cartId, lineItemId);
      }
      const { cart } = await medusa.store.cart.updateLineItem(
        cartId,
        lineItemId,
        { quantity },
      );
      return cart;
    },
    onMutate: async ({ lineItemId, quantity }) => {
      const cartId = useCartStore.getState().cartId;
      if (!cartId) return undefined;
      const queryKey = cartKeys.detail(cartId);
      await queryClient.cancelQueries({ queryKey });
      const previousCart =
        queryClient.getQueryData<StoreCart | null>(queryKey) ?? null;
      if (!previousCart) return { previousCart: null, queryKey };

      const items = previousCart.items ?? [];
      const nextItems =
        quantity <= 0
          ? items.filter((l) => l.id !== lineItemId)
          : items.map((l) => (l.id === lineItemId ? { ...l, quantity } : l));

      queryClient.setQueryData<StoreCart>(queryKey, {
        ...previousCart,
        items: nextItems,
      });
      return { previousCart, queryKey };
    },
    onError: (_err, _input, context) => {
      if (context) {
        queryClient.setQueryData(context.queryKey, context.previousCart);
      }
    },
    onSettled: () => {
      if (queryClient.isMutating({ mutationKey: CART_MUTATION_KEY }) === 1) {
        return queryClient.invalidateQueries({ queryKey: cartKeys.all });
      }
    },
  });
}

export function useRemoveCartLine(): UseMutationResult<
  StoreCart,
  Error,
  { lineItemId: string },
  CartMutationContext | undefined
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: CART_MUTATION_KEY,
    mutationFn: async ({ lineItemId }) => {
      const cartId = useCartStore.getState().cartId;
      if (!cartId) throw new Error("No cart");
      return deleteLineAndGetCart(cartId, lineItemId);
    },
    onMutate: async ({ lineItemId }) => {
      const cartId = useCartStore.getState().cartId;
      if (!cartId) return undefined;
      const queryKey = cartKeys.detail(cartId);
      await queryClient.cancelQueries({ queryKey });
      const previousCart =
        queryClient.getQueryData<StoreCart | null>(queryKey) ?? null;
      if (!previousCart) return { previousCart: null, queryKey };

      const items = previousCart.items ?? [];
      const nextItems = items.filter((l) => l.id !== lineItemId);

      queryClient.setQueryData<StoreCart>(queryKey, {
        ...previousCart,
        items: nextItems,
      });
      return { previousCart, queryKey };
    },
    onError: (_err, _input, context) => {
      if (context) {
        queryClient.setQueryData(context.queryKey, context.previousCart);
      }
    },
    onSettled: () => {
      if (queryClient.isMutating({ mutationKey: CART_MUTATION_KEY }) === 1) {
        return queryClient.invalidateQueries({ queryKey: cartKeys.all });
      }
    },
  });
}

export function useSetFamilyBook(): (
  targetVariantId: string | null,
  familyVariantIds: Set<string>,
) => Promise<void> {
  const { data: cart } = useCart();
  const addToCart = useAddToCart();
  const removeLine = useRemoveCartLine();

  return async (targetVariantId, familyVariantIds) => {
    if (targetVariantId === null) return;
    const familyLines = (cart?.items ?? []).filter(
      (l) => l.variant_id != null && familyVariantIds.has(l.variant_id),
    );
    const currentVariantId =
      familyLines.find((l) => l.variant_id != null)?.variant_id ?? null;

    if (familyLines.length === 1 && currentVariantId === targetVariantId) return;

    for (const line of familyLines) {
      await removeLine.mutateAsync({ lineItemId: line.id });
    }

    await addToCart.mutateAsync({ variant_id: targetVariantId, quantity: 1 });
  };
}

export function useUpdateCart(): UseMutationResult<
  StoreCart,
  Error,
  StoreUpdateCart
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body) => {
      const cartId = useCartStore.getState().cartId;
      if (!cartId) throw new Error("No cart");
      const { cart } = await medusa.store.cart.update(cartId, body);
      return cart;
    },
    onSuccess: (cart) => {
      queryClient.setQueryData(cartKeys.detail(cart.id), cart);
    },
  });
}

const attachImpl: AttachCartToCustomer = async () => {
  const state = useCartStore.getState();
  const cartId = state.cartId;
  if (!cartId) return;
  try {
    const { cart } = await medusa.store.cart.transferCart(cartId);
    state.setCartId(cart.id);
  } catch {
    state.clearCart();
  }
};

setAttachCartToCustomerImpl(attachImpl);

export function useFetchConvenienceFee(pmName:string, total:number, countryCode: string) {
  return useQuery({
    queryKey: ["convenienceFee", pmName, countryCode, total],
    queryFn: () => computeConvenienceFee(pmName, total, countryCode),
  })
}

